import React, { useEffect, useState, useRef } from 'react';
import { promoAPI } from '../../services/api';

const IMPRESSION_SESSION_KEY = 'promo_impressions_logged';

const getLoggedImpressions = () => {
  try {
    const raw = sessionStorage.getItem(IMPRESSION_SESSION_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const markImpressionLogged = (id) => {
  try {
    const logged = getLoggedImpressions();
    logged.add(id);
    sessionStorage.setItem(IMPRESSION_SESSION_KEY, JSON.stringify([...logged]));
  } catch {
    // sessionStorage unavailable — silently skip dedupe
  }
};

const pickOne = (placements) => {
  if (!placements || placements.length === 0) return null;
  if (placements.length === 1) return placements[0];
  return placements[Math.floor(Math.random() * placements.length)];
};

const PromoSlot = ({ slotId, variant = 'card', style = {}, placement: providedPlacement = null }) => {
  const [placement, setPlacement] = useState(providedPlacement);
  const [loading, setLoading] = useState(!providedPlacement);
  const impressionLoggedRef = useRef(false);

  useEffect(() => {
    // When a placement is supplied (e.g. by PromoGroup, which picks distinct
    // ads), render it directly and skip the per-slot fetch/random pick.
    if (providedPlacement) {
      setPlacement(providedPlacement);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await promoAPI.getActiveBySlot(slotId);
        if (cancelled) return;
        setPlacement(pickOne(data.data));
      } catch (err) {
        if (!cancelled) setPlacement(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slotId, providedPlacement]);

  useEffect(() => {
    if (!placement || impressionLoggedRef.current) return;
    const logged = getLoggedImpressions();
    if (logged.has(placement._id)) return;

    impressionLoggedRef.current = true;
    markImpressionLogged(placement._id);
    promoAPI.trackImpression(placement._id).catch(() => {
      // swallow — never block UI on a tracking error
    });
  }, [placement]);

  if (loading || !placement) return null;

  const handleClick = () => {
    promoAPI.trackClick(placement._id).catch(() => {});
  };

  const sponsoredLabel = placement.isSponsored ? (
    <span
      style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '0.5px',
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        zIndex: 2,
      }}
    >
      Sponsored
    </span>
  ) : null;

  if (variant === 'banner') {
    return (
      <a
        href={placement.destinationUrl}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${placement.title} — opens in new tab`}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '16px 20px',
          background: 'var(--cardBackground)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          textDecoration: 'none',
          color: 'inherit',
          boxShadow: 'var(--shadow-sm)',
          marginBottom: '20px',
          ...style,
        }}
      >
        {sponsoredLabel}
        <img
          src={placement.imageUrl}
          alt={placement.title}
          style={{
            width: '96px',
            height: '96px',
            objectFit: 'cover',
            borderRadius: '8px',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', marginBottom: '4px' }}>
            {placement.title}
          </div>
          {placement.description && (
            <div style={{ fontSize: '13px', color: 'var(--textSecondary)', lineHeight: 1.4 }}>
              {placement.description}
            </div>
          )}
        </div>
        <span
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            background: 'var(--primary)',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {placement.ctaLabel || 'Learn more'}
        </span>
      </a>
    );
  }

  if (variant === 'inline') {
    return (
      <a
        href={placement.destinationUrl}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${placement.title} — opens in new tab`}
        style={{
          position: 'relative',
          display: 'flex',
          gap: '16px',
          padding: '16px',
          background: 'var(--cardBackground)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          textDecoration: 'none',
          color: 'inherit',
          ...style,
        }}
      >
        {sponsoredLabel}
        <img
          src={placement.imageUrl}
          alt={placement.title}
          style={{
            width: '80px',
            height: '80px',
            objectFit: 'cover',
            borderRadius: '8px',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)', marginBottom: '4px' }}>
            {placement.title}
          </div>
          {placement.description && (
            <div style={{ fontSize: '13px', color: 'var(--textSecondary)', lineHeight: 1.4, marginBottom: '8px' }}>
              {placement.description}
            </div>
          )}
          <span
            style={{
              display: 'inline-block',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--primary)',
            }}
          >
            {placement.ctaLabel || 'Learn more'} →
          </span>
        </div>
      </a>
    );
  }

  // Default: card variant — matches product card size in masonry grid
  return (
    <a
      href={placement.destinationUrl}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${placement.title} — opens in new tab`}
      style={{
        position: 'relative',
        display: 'block',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'var(--cardBackground)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        textDecoration: 'none',
        color: 'inherit',
        ...style,
      }}
    >
      {sponsoredLabel}
      <img
        src={placement.imageUrl}
        alt={placement.title}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
        }}
      />
      <div style={{ padding: '12px' }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--text)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.3,
            marginBottom: placement.description ? '6px' : '10px',
          }}
        >
          {placement.title}
        </div>
        {placement.description && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--textSecondary)',
              lineHeight: 1.4,
              marginBottom: '10px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {placement.description}
          </div>
        )}
        <span
          style={{
            display: 'inline-block',
            padding: '6px 12px',
            borderRadius: '6px',
            background: 'var(--primary)',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {placement.ctaLabel || 'Learn more'}
        </span>
      </div>
    </a>
  );
};

// Renders up to `count` DISTINCT promos for a slot. Fetches the pool once and
// picks different placements, so the same ad never shows twice at the same time.
// Each card is wrapped for the masonry feed (break-inside: avoid).
export const PromoGroup = ({ slotId, count = 2, variant = 'card', itemStyle = {} }) => {
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    promoAPI
      .getActiveBySlot(slotId)
      .then(({ data }) => {
        if (cancelled) return;
        const pool = [...(data?.data || [])];
        // Fisher–Yates shuffle, then take the first `count` distinct placements.
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        setPicks(pool.slice(0, count));
      })
      .catch(() => {
        if (!cancelled) setPicks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slotId, count]);

  return picks.map((p) => (
    <div key={p._id} style={{ breakInside: 'avoid', marginBottom: '16px' }}>
      <PromoSlot slotId={slotId} placement={p} variant={variant} style={itemStyle} />
    </div>
  ));
};

export default PromoSlot;
